import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Upload, Check, AlertCircle, Loader2, FileText, Zap, Map as MapIcon, ClipboardList, ArrowRight, Sparkles, Home } from "lucide-react";
import { apiClient } from "../lib/api";
import { trackInitiateCheckout } from "../lib/fbpixel";
import Header from "../components/Header";
import RoofMap from "../components/RoofMap";

const ALL_STEPS = [
  { id: "bill", labelKey: "portal.step_bill", icon: FileText },
  { id: "electrical", labelKey: "portal.step_electrical", icon: Zap },
  { id: "roof", labelKey: "portal.step_roof", icon: MapIcon },
  { id: "dwelling", labelKey: "portal.step_dwelling", icon: Home },
  { id: "fiscal", labelKey: "portal.step_fiscal", icon: ClipboardList },
];

/**
 * Summer-Sale leads (funnel='summer-sale') have already completed
 * dwelling + fiscal + phase via the new single-page intake at /intake.
 * For them we only need Bill + Roof in the Portal — drastically reduces
 * friction and confusion ("waarom moet ik dit nog een keer?").
 */
const stepsForLead = (lead) => {
  if (lead?.funnel === "summer-sale") {
    return ALL_STEPS.filter((s) => s.id === "bill" || s.id === "roof");
  }
  return ALL_STEPS;
};

export default function Portal() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [active, setActive] = useState("bill");
  const [busy, setBusy] = useState(false);
  const [roi, setRoi] = useState(null);

  const STEPS = stepsForLead(lead);

  const reload = async () => {
    if (!token) return;
    try {
      const r = await apiClient.get("/leads/me", { params: { token } });
      setLead(r.data);
      // Determine first incomplete step (using the steps for this lead's funnel)
      const visible = stepsForLead(r.data);
      const next = visible.find((s) => !r.data.intake?.[s.id]);
      if (next) setActive(next.id);
      else setActive("roi");
    } catch (e) {
      toast.error(t("portal.token_invalid"));
      setTimeout(() => nav("/"), 1500);
    }
  };

  useEffect(() => { reload(); }, [token]);

  const completed = (id) => !!lead?.intake?.[id];
  const allDone = STEPS.every((s) => completed(s.id));

  const upload = async (endpoint, file, key) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", file);
      const r = await apiClient.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Análisis completado");
      await reload();
      return r.data;
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const saveRoof = async (polygon, pitch_deg, orientation) => {
    if (polygon.length < 3) return toast.error(t("portal.roof.min_points"));
    setBusy(true);
    try {
      await apiClient.post(`/intake/roof?token=${token}`, { polygon, pitch_deg, orientation });
      toast.success(t("portal.roof.saved"));
      await reload();
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const saveFiscal = async (form) => {
    setBusy(true);
    try {
      await apiClient.post(`/intake/fiscal?token=${token}`, form);
      toast.success(t("portal.fiscal.saved"));
      await reload();
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const saveDwelling = async (form) => {
    setBusy(true);
    try {
      await apiClient.post(`/intake/dwelling?token=${token}`, form);
      toast.success(t("portal.dwelling.saved"));
      await reload();
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setBusy(false); }
  };

  const computeROI = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get("/intake/roi", { params: { token } });
      setRoi(r.data);
    } catch (e) { toast.error("Error: " + e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (allDone) computeROI(); }, [allDone, lead?.intake?.fiscal, lead?.intake?.roof?.area_m2]);

  const goPay = async (promoCode = null) => {
    setBusy(true);
    try {
      trackInitiateCheckout({
        content_name: "DoctorSol Dossier",
        content_category: "report",
      });
      const r = await apiClient.post("/payments/checkout", {
        lead_id: lead.id, origin_url: window.location.origin,
        promo_code: promoCode || null,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error("Error: " + (e?.response?.data?.detail || e.message)); setBusy(false); }
  };

  const downloadPDF = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/report/${lead.id}/pdf?token=${token}`, "_blank");
  };

  if (!token) return <div className="p-10 text-center">{t("portal.missing_token")}</div>;
  if (!lead) return <div className="p-10 text-center text-[#475569]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const progress = STEPS.filter((s) => completed(s.id)).length;
  const pct = (progress / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <Header minimal />
      {lead.auto_promo_code === "INTRO100" && !lead.paid && (
        <div data-testid="intro100-banner"
          className="bg-[#FCBF49] text-[#003049] py-2.5 text-center text-sm font-semibold flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" /> Introductietarief actief — € 100 korting wordt automatisch toegepast bij betaling
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-12 gap-6 md:gap-10">
        {/* Sidebar */}
        <aside className="md:col-span-4 lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] mb-2">Hola</div>
          <h1 className="serif text-3xl text-[#0F172A] mb-6">{lead.name}</h1>
          <div className="bg-white border border-[#FFF7ED] rounded-xl p-5 mb-6">
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-xs uppercase tracking-wider text-[#E78938]">Progreso</span>
              <span className="serif text-2xl text-[#E78938] tnum">{progress}/{STEPS.length}</span>
            </div>
            <div className="h-1.5 bg-[#FFF7ED] rounded-full overflow-hidden">
              <div className="h-full bg-[#E78938] transition-all" style={{ width: `${pct}%` }} data-testid="progress-bar" />
            </div>
          </div>
          <nav className="space-y-1.5">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const done = completed(s.id);
              const isActive = active === s.id;
              return (
                <button key={s.id} data-testid={`step-${s.id}`} onClick={() => setActive(s.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all
                    ${isActive ? "bg-[#0F172A] text-[#FFFFFF]" : "bg-white hover:bg-[#FFF7ED]/60 text-[#0F172A] border border-[#FFF7ED]"}`}>
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center
                    ${done ? "bg-[#E78938] text-white" : isActive ? "bg-[#E78938] text-white" : "bg-[#FFF7ED] text-[#475569]"}`}>
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="flex-1">{t(s.labelKey)}</span>
                </button>
              );
            })}
            {allDone && (
              <button data-testid="step-roi" onClick={() => setActive("roi")}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all mt-4
                  ${active === "roi" ? "bg-[#E78938] text-white" : "bg-[#FED7AA] text-[#0F172A]"}`}>
                <Sparkles className="w-4 h-4" />
                <span className="flex-1">{t("portal.your_dossier")}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </nav>
        </aside>

        {/* Main */}
        <main className="md:col-span-8 lg:col-span-9">
          <div className="bg-white rounded-xl border border-[#FFF7ED] p-6 md:p-10 min-h-[500px]" data-testid="active-step-panel">
            {active === "bill" && <BillStep busy={busy} data={lead.intake?.bill} onUpload={(f) => upload("/intake/bill", f)} lead={lead} token={token} onRegionChange={reload} />}
            {active === "electrical" && <ElectricalStep busy={busy} data={lead.intake?.electrical} onUpload={(f) => upload("/intake/electrical", f)} />}
            {active === "roof" && <RoofStep busy={busy} data={lead.intake?.roof} bill={lead.intake?.bill} onSave={saveRoof} />}
            {active === "dwelling" && <DwellingStep busy={busy} data={lead.intake?.dwelling} onSave={saveDwelling} />}
            {active === "fiscal" && <FiscalStep busy={busy} data={lead.intake?.fiscal} onSave={saveFiscal} />}
            {active === "roi" && <ROISection roi={roi} lead={lead} onPay={goPay} onDownload={downloadPDF} busy={busy} />}
          </div>
        </main>
      </div>
    </div>
  );
}

function UploadCard({ label, accept, onUpload, busy, data, render }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] mb-2">{label}</div>
      <input type="file" accept={accept} onChange={(e) => setFile(e.target.files?.[0])}
        data-testid="file-input"
        className="block w-full text-sm text-[#475569] file:mr-4 file:rounded-xl file:border-0 file:bg-[#FFF7ED] file:text-[#0F172A] file:px-4 file:py-2.5 file:font-medium hover:file:bg-[#FED7AA] transition" />
      <button data-testid="btn-analyze" onClick={() => file && onUpload(file)} disabled={!file || busy}
        className="mt-4 bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 font-medium disabled:opacity-50 transition-all flex items-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />{t("portal.bill.analyzing")}</> : <><Upload className="w-4 h-4" />{t("portal.bill.analyze")}</>}
      </button>
      {data && (
        <div className="mt-6 bg-[#FFF7ED]/50 rounded-xl p-5" data-testid="result-card">
          {render(data)}
        </div>
      )}
    </div>
  );
}

function BillStep({ busy, data, onUpload, lead, token, onRegionChange }) {
  const { t } = useTranslation();
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    apiClient.get("/regions").then((r) => setRegions(r.data.regions));
  }, []);

  const applyRegion = async (region) => {
    try {
      await apiClient.patch(`/leads/region?token=${token}&region=${region}`);
      toast.success("OK");
      onRegionChange?.();
    } catch (e) { toast.error("Error: " + e.message); }
  };

  const suggestion = data?.suggested_region_change;

  return (
    <>
      <h2 className="serif text-3xl text-[#0F172A] mb-3">{t("portal.bill.title")}</h2>
      <p className="text-[#475569] mb-3">{t("portal.bill.subtitle")}</p>

      <div className="mb-6 flex items-center gap-3 text-sm">
        <span className="text-[#E78938] uppercase tracking-wider text-xs">{t("portal.roi.region")}:</span>
        <select data-testid="region-select" value={lead?.region || ""}
          onChange={(e) => applyRegion(e.target.value)}
          className="bg-white border border-[#FFF7ED] rounded-lg px-3 py-1.5 text-sm">
          {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      {suggestion && (
        <div className="mb-5 p-4 rounded-xl bg-[#E78938]/10 border border-[#E78938]/30 flex justify-between items-start gap-4" data-testid="region-suggestion">
          <div className="text-sm">
            <b className="text-[#E78938]">{t("portal.bill.region_change_q")}</b><br />
            <b>{suggestion.province}</b> → {suggestion.region_id}
          </div>
          <button data-testid="apply-region" onClick={() => applyRegion(suggestion.region_id)}
            className="bg-[#E78938] text-white rounded-lg px-4 py-2 text-sm whitespace-nowrap">
            {t("portal.bill.region_change_yes")}
          </button>
        </div>
      )}

      <UploadCard label={t("portal.bill.label")} accept="image/*,.pdf" onUpload={onUpload} busy={busy} data={data}
        render={(d) => (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row k={t("portal.bill.cups")} v={d.cups || "—"} />
            <Row k={t("portal.bill.tariff")} v={d.tariff || "—"} />
            <Row k={t("portal.bill.supplier")} v={d.supplier || "—"} />
            <Row k="kW" v={d.contracted_power_kw ? `${d.contracted_power_kw} kW` : "—"} />
            <Row k={t("portal.bill.annual_kwh")} v={d.annual_kwh ? `${Math.round(d.annual_kwh).toLocaleString()} kWh` : "—"} />
            <Row k="CP" v={d.postal_code || "—"} />
            <Row k={t("portal.bill.city")} v={d.city || "—"} />
            <Row k="Provincia" v={d.province || "—"} />
            <Row k="Confianza" v={d.confidence || "—"} />
          </div>
        )} />
    </>
  );
}

function ElectricalStep({ busy, data, onUpload }) {
  const { t } = useTranslation();
  return (
    <>
      <h2 className="serif text-3xl text-[#0F172A] mb-3">{t("portal.electrical.title")}</h2>
      <p className="text-[#475569] mb-6">{t("portal.electrical.subtitle")}</p>
      <UploadCard label={t("portal.electrical.label")} accept="image/*" onUpload={onUpload} busy={busy} data={data}
        render={(d) => (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {d.phase_type === "trifasico" ? (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E78938] text-white">
                  <Zap className="w-4 h-4" />{t("portal.electrical.trifasico").toUpperCase()}
                </span>
              ) : d.phase_type === "monofasico" ? (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E78938] text-white">
                  <Zap className="w-4 h-4" />{t("portal.electrical.monofasico").toUpperCase()}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFF7ED] text-[#475569]">
                  <AlertCircle className="w-4 h-4" />—
                </span>
              )}
              <span className="text-xs text-[#E78938] uppercase">{d.confidence}</span>
            </div>
            <p className="text-sm text-[#475569]">{d.reasoning}</p>
            {d.main_breaker_amps && <Row k={t("portal.electrical.main_breaker")} v={`${d.main_breaker_amps} A`} />}
          </div>
        )} />
    </>
  );
}

function RoofStep({ busy, data, bill, onSave }) {
  const { t } = useTranslation();
  const [poly, setPoly] = useState(data?.polygon || []);
  const [pitch, setPitch] = useState(data?.pitch_deg || 25);
  const [orientation, setOrientation] = useState(data?.orientation || "Sur");
  const orientOpts = ["Norte","Noreste","Este","Sureste","Sur","Suroeste","Oeste","Noroeste"];
  const initialCenter = (bill?.lat && bill?.lng) ? [bill.lat, bill.lng] : null;
  const billAddress = [bill?.supply_address, bill?.city, bill?.postal_code]
    .filter(Boolean).join(", ");
  const remaining = 3 - poly.length;
  return (
    <>
      <h2 className="serif text-3xl text-[#0F172A] mb-3">{t("portal.roof.title")}</h2>
      <p className="text-[#475569] mb-6">{t("portal.roof.subtitle")}</p>
      <RoofMap initial={data?.polygon || []} onChange={setPoly} initialCenter={initialCenter} initialAddress={billAddress} />
      {poly.length < 3 && (
        <div className="mt-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 text-sm text-[#475569]" data-testid="roof-help">
          <strong className="text-[#0F172A]">{t("portal.roof.draw_intro")}</strong> {t("portal.roof.draw_help")}
        </div>
      )}
      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            {t("portal.roof.pitch")}: <span className="text-[#E78938] tnum font-semibold">{pitch}°</span>
          </label>
          <input data-testid="pitch-slider" type="range" min="5" max="45" step="5"
            value={pitch} onChange={(e) => setPitch(Number(e.target.value))}
            className="w-full accent-[#E78938]" />
          <div className="flex justify-between text-xs text-[#94A3B8] mt-1"><span>{t("portal.roof.pitch_5")}</span><span>{t("portal.roof.pitch_25")}</span><span>{t("portal.roof.pitch_45")}</span></div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">{t("portal.roof.orientation")}</label>
          <select data-testid="orientation-select" value={orientation} onChange={(e)=>setOrientation(e.target.value)}
            className="w-full bg-[#FFF7ED] rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#E78938]/40">
            {orientOpts.map(o => <option key={o} value={o}>{t(`portal.roof.orientations.${o}`, o)}</option>)}
          </select>
          <div className="text-xs text-[#94A3B8] mt-1">{t("portal.roof.orientation_help")}</div>
        </div>
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <button data-testid="save-roof" disabled={busy || poly.length < 3}
          onClick={() => onSave(poly, pitch, orientation)}
          className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 disabled:bg-[#FFE4CC] disabled:text-[#C5701D] disabled:cursor-not-allowed transition-all flex items-center gap-2 justify-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {poly.length < 3
            ? (remaining === 1 ? t("portal.roof.save_btn_1_point") : t("portal.roof.save_btn_n_points", { n: remaining }))
            : t("portal.roof.save_btn_ready")}
        </button>
        {data && (
          <div className="text-sm text-[#475569]">
            {t("portal.roof.area")}: <b className="text-[#0F172A]">{data.area_m2} m²</b> · {t("portal.roof.panels_possible")}: <b className="text-[#0F172A]">{data.panel_count}</b>
            {data.orientation_auto_detected && data.orientation_auto_detected !== data.orientation && (
              <span className="ml-2 text-[10px] text-[#E78938]">(auto: {data.orientation_auto_detected})</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function DwellingStep({ busy, data, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    m2: data?.m2 ?? "",
    bathrooms: data?.bathrooms ?? "",
    persons: data?.persons ?? "",
    is_primary: data?.is_primary ?? true,
    construction_year: data?.construction_year ?? "",
    want_battery: data?.want_battery ?? false,
  });
  const [overcap, setOvercap] = useState(false);

  const submit = () => {
    const m2 = Number(form.m2);
    const persons = Number(form.persons);
    if (!m2 || m2 < 25) return toast.error(t("portal.dwelling.err_m2_min"));
    if (m2 > 300) { setOvercap("m2"); return; }
    if (!persons || persons < 1) return toast.error(t("portal.dwelling.err_persons_min"));
    if (persons > 10) { setOvercap("persons"); return; }
    onSave({
      m2, bathrooms: Number(form.bathrooms) || 1, persons,
      is_primary: form.is_primary,
      construction_year: form.construction_year ? Number(form.construction_year) : null,
      want_battery: form.want_battery,
    });
  };

  return (
    <>
      <h2 className="serif text-3xl text-[#0F172A] mb-3">{t("portal.dwelling.title")}</h2>
      <p className="text-[#475569] mb-6">{t("portal.dwelling.subtitle")}</p>

      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput data-testid="d-m2" label={t("portal.dwelling.m2")} type="number" min={25} max={300}
          value={form.m2} onChange={(v) => setForm({ ...form, m2: v })} suffix="m²" />
        <FieldInput data-testid="d-baths" label={t("portal.dwelling.bathrooms")} type="number" min={0} max={10}
          value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} />
        <FieldInput data-testid="d-persons" label={t("portal.dwelling.persons")} type="number" min={1} max={15}
          value={form.persons} onChange={(v) => setForm({ ...form, persons: v })} />
        <FieldInput data-testid="d-year" label={t("portal.dwelling.construction_year")} type="number" min={1900} max={2030}
          value={form.construction_year} onChange={(v) => setForm({ ...form, construction_year: v })} placeholder="bv. 1995" />
      </div>

      <div className="mt-4 space-y-3">
        <ToggleRow testid="d-primary" checked={form.is_primary}
          onClick={() => setForm({ ...form, is_primary: !form.is_primary })}
          label={t("portal.dwelling.is_primary_label")} desc={t("portal.dwelling.is_primary_desc")} />
        <ToggleRow testid="d-battery" checked={form.want_battery}
          onClick={() => setForm({ ...form, want_battery: !form.want_battery })}
          label={t("portal.dwelling.battery_label")} desc={t("portal.dwelling.battery_desc")} />
      </div>

      <button data-testid="save-dwelling" onClick={submit} disabled={busy}
        className="mt-6 bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 disabled:opacity-50 transition-all flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {t("portal.dwelling.save_btn")}
      </button>

      {overcap && (
        <PersonalConsultModal type={overcap} onClose={() => setOvercap(false)} />
      )}
    </>
  );
}

function FieldInput({ label, value, onChange, suffix, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#E78938] mb-1.5 font-semibold">{label}</label>
      <div className="relative">
        <input {...rest} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-[#0F172A] outline-none focus:bg-white focus:border-[#E78938] focus:ring-2 focus:ring-[#E78938]/40 transition" />
        {suffix && <span className="absolute right-3 top-3 text-sm text-[#94A3B8]">{suffix}</span>}
      </div>
    </div>
  );
}

function ToggleRow({ checked, onClick, label, desc, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full text-left p-5 rounded-xl border transition-all flex justify-between items-start gap-4
        ${checked ? "border-[#E78938] bg-[#E78938]/5" : "border-[#FFF7ED] bg-white hover:border-[#E78938]/40"}`}>
      <div>
        <div className="font-medium text-[#0F172A]">{label}</div>
        {desc && <div className="text-sm text-[#475569] mt-0.5">{desc}</div>}
      </div>
      <div className={`w-12 h-7 rounded-full p-0.5 transition-all ${checked ? "bg-[#E78938]" : "bg-[#FFF7ED]"}`}>
        <div className={`w-6 h-6 rounded-full bg-white transition-all ${checked ? "translate-x-5" : ""}`} />
      </div>
    </button>
  );
}

function PersonalConsultModal({ type, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="personal-consult-modal">
      <div className="bg-white rounded-2xl max-w-md p-7" onClick={(e) => e.stopPropagation()}>
        <AlertCircle className="w-10 h-10 text-[#E78938] mb-3" strokeWidth={1.5} />
        <h3 className="serif text-2xl text-[#0F172A] mb-2">{t("portal.personal_consult.title")}</h3>
        <p className="text-sm text-[#475569] mb-5">{t(`portal.personal_consult.${type}`)}</p>
        <a href="mailto:info@doctorsol.es" className="inline-block bg-[#E78938] text-white rounded-xl px-5 py-3 text-sm font-medium">
          {t("portal.personal_consult.contact_btn")}
        </a>
        <button onClick={onClose} className="ml-3 text-sm text-[#475569] hover:underline">
          {t("portal.personal_consult.close")}
        </button>
      </div>
    </div>
  );
}

function FiscalStep({ busy, data, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    is_resident: data?.is_resident ?? true,
    has_ev: data?.has_ev ?? false,
    wp_water: data?.wp_water ?? false,
    wp_heating: data?.wp_heating ?? (data?.has_heatpump ?? false),
    has_pool: data?.has_pool ?? false,
    pool: data?.pool ?? { m3: "", covered: false },
  });
  const [overcap, setOvercap] = useState(false);
  const toggle = (k) => setForm({ ...form, [k]: !form[k] });

  const submit = () => {
    const payload = {
      is_resident: form.is_resident,
      has_ev: form.has_ev,
      wp_water: form.wp_water,
      wp_heating: form.wp_heating,
      has_heatpump: form.wp_heating,  // legacy compat
      has_pool: form.has_pool,
      pool: form.has_pool
        ? { m3: Number(form.pool.m3) || 0, covered: !!form.pool.covered }
        : null,
    };
    if (form.has_pool && Number(form.pool.m3) > 200) { setOvercap("pool_m3"); return; }
    onSave(payload);
  };

  const items = [
    { k: "is_resident", label: t("portal.fiscal.is_resident_label"), desc: t("portal.fiscal.is_resident_desc") },
    { k: "has_ev", label: t("portal.fiscal.has_ev_label"), desc: t("portal.fiscal.has_ev_desc_v2") },
    { k: "wp_water", label: t("portal.fiscal.wp_water_label"), desc: t("portal.fiscal.wp_water_desc") },
    { k: "wp_heating", label: t("portal.fiscal.wp_heating_label"), desc: t("portal.fiscal.wp_heating_desc") },
    { k: "has_pool", label: t("portal.fiscal.has_pool_label"), desc: t("portal.fiscal.has_pool_desc") },
  ];
  return (
    <>
      <h2 className="serif text-3xl text-[#0F172A] mb-3">{t("portal.fiscal.title")}</h2>
      <p className="text-[#475569] mb-6">{t("portal.fiscal.subtitle")}</p>
      <div className="space-y-3">
        {items.map((it) => (
          <ToggleRow key={it.k} testid={`toggle-${it.k}`} checked={form[it.k]}
            onClick={() => toggle(it.k)} label={it.label} desc={it.desc} />
        ))}
        {form.has_pool && (
          <div data-testid="pool-subfields" className="ml-4 pl-5 border-l-2 border-[#E78938] space-y-3 py-2">
            <FieldInput data-testid="pool-m3" label={t("portal.fiscal.pool_m3_label")} type="number" min={1} max={200} suffix="m³"
              value={form.pool.m3} onChange={(v) => setForm({ ...form, pool: { ...form.pool, m3: v } })} />
            <ToggleRow testid="pool-covered" checked={form.pool.covered}
              onClick={() => setForm({ ...form, pool: { ...form.pool, covered: !form.pool.covered } })}
              label={t("portal.fiscal.pool_covered_label")} desc={t("portal.fiscal.pool_covered_desc")} />
          </div>
        )}
      </div>
      <button data-testid="save-fiscal" onClick={submit} disabled={busy}
        className="mt-6 bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3 disabled:opacity-50 transition-all flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {t("portal.fiscal.save_calc_roi")}
      </button>
      {overcap && <PersonalConsultModal type={overcap} onClose={() => setOvercap(false)} />}
    </>
  );
}

function ROISection({ roi, lead, onPay, onDownload, busy }) {
  const { t } = useTranslation();
  const [reportPrice, setReportPrice] = useState(89);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  useEffect(() => {
    apiClient.get("/calc/report-price").then((r) => setReportPrice(r.data.report_price_eur)).catch(() => {});
  }, []);
  // Auto-apply the friend referral promo when the lead came in via a referral.
  useEffect(() => {
    if (!lead?.auto_promo_code || lead.paid || promo?.ok) return;
    const code = lead.auto_promo_code;
    setPromoCode(code);
    apiClient.post("/payments/promo/validate", { code }).then((r) => {
      if (r.data?.ok) setPromo(r.data);
    }).catch(() => {});
  }, [lead?.auto_promo_code, lead?.paid]);
  const validatePromo = async () => {
    if (!promoCode.trim()) { setPromo(null); return; }
    setValidatingPromo(true);
    try {
      const r = await apiClient.post("/payments/promo/validate", { code: promoCode.trim() });
      if (r.data.ok) {
        setPromo(r.data);
        toast.success(`-€${r.data.discount_eur} aplicado`);
      } else {
        setPromo({ ok: false, reason: r.data.reason });
        toast.error("Código no válido");
      }
    } catch (e) { toast.error("Error validando"); }
    finally { setValidatingPromo(false); }
  };
  const shareUrl = lead?.referral_code ? `${window.location.origin}/?ref=${lead.referral_code}` : "";
  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1500);
    } catch { /* clipboard may be unavailable */ }
  };
  if (!roi) return <div className="text-center py-10 text-[#475569]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /> {t("portal.roi.loading")}</div>;
  const finalPrice = promo?.ok ? promo.final_eur : reportPrice;
  return (
    <div data-testid="roi-section">
      <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] mb-2">{t("portal.roi.kicker")}</div>
      <h2 className="serif text-4xl text-[#0F172A] mb-2">{t("portal.roi.title", { name: lead.name.split(" ")[0] })}</h2>
      <p className="text-[#475569] mb-8">{t("portal.roi.region")}: <b>{roi.region}</b></p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label={t("portal.roi.system")} value={`${roi.system_kwp} kWp`} />
        <Stat label={t("portal.roi.production")} value={`${roi.annual_production_kwh.toLocaleString()} kWh`} accent />
        <Stat label={t("portal.roi.savings_year")} value={`€ ${roi.annual_savings_eur.toLocaleString()}`} accent />
        <Stat label={t("portal.roi.payback")} value={`${roi.payback_years} ${t("landing.years")}`} />
      </div>

      <div className="bg-[#0F172A] text-[#FFFFFF] rounded-xl p-7 grid md:grid-cols-3 gap-6 mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#FED7AA]">{t("portal.roi.gross_cost")}</div>
          <div className="serif text-3xl tnum mt-1">€ {roi.gross_cost_eur.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#FED7AA]">{t("portal.roi.subsidies")}</div>
          <div className="serif text-3xl text-[#E78938] tnum mt-1">
            – € {(roi.subsidies.eu_grant_eur + roi.subsidies.irpf_deduction_eur + roi.subsidies.ibi_savings_eur).toLocaleString()}
          </div>
          <div className="text-xs text-[#FFFFFF]/60 mt-1">EU + IRPF + IBI</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#FED7AA]">{t("portal.roi.net_cost")}</div>
          <div className="serif text-3xl text-white tnum mt-1">€ {roi.net_cost_eur.toLocaleString()}</div>
        </div>
      </div>

      {roi.sizing && (
        <div data-testid="sizing-summary" className="bg-white border border-[#FFF7ED] rounded-xl p-6 mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#E78938] mb-3">{t("portal.roi.sizing_title")}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {roi.sizing.inverter && (
              <SizingRow k={t("portal.roi.sizing_inverter")} v={`${roi.sizing.inverter.kw} kW ${roi.sizing.inverter.type} — €${roi.sizing.inverter.price.toLocaleString()}`} />
            )}
            {roi.sizing.panels && (
              <SizingRow k={t("portal.roi.sizing_panels")} v={`${roi.sizing.panels.count} × 500 Wp (${roi.sizing.panels.kwp} kWp)`} />
            )}
            {roi.sizing.battery && (
              <SizingRow k={t("portal.roi.sizing_battery")} v={`${roi.sizing.battery.kwh} kWh ${roi.sizing.battery.type} — €${roi.sizing.battery.price.toLocaleString()}`} />
            )}
            {roi.sizing.heatpump_water && (
              <SizingRow k={t("portal.roi.sizing_wp_water")} v={`${roi.sizing.heatpump_water.kw} kW — €${roi.sizing.heatpump_water.price.toLocaleString()}`} />
            )}
            {roi.sizing.heatpump_verwarming && (
              <SizingRow k={t("portal.roi.sizing_wp_heating")} v={`${roi.sizing.heatpump_verwarming.kw} kW (${roi.sizing.heatpump_verwarming.label}) — €${roi.sizing.heatpump_verwarming.price.toLocaleString()}`} />
            )}
            {roi.sizing.pool_pump && (
              <SizingRow k={t("portal.roi.sizing_pool")} v={`${roi.sizing.pool_pump.m3} m³ — €${roi.sizing.pool_pump.install_price.toLocaleString()}`} />
            )}
            <SizingRow k={t("portal.roi.sizing_total")} v={`€ ${roi.sizing.total_investment_eur.toLocaleString()}`} accent />
          </div>
          {roi.sizing.phase_recommend_upgrade && (
            <div className="mt-4 text-xs text-[#C5701D] bg-[#FFF7ED] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{t("portal.roi.sizing_phase_upgrade")}</span>
            </div>
          )}
          {roi.sizing.warnings?.length > 0 && (
            <div className="mt-3 text-xs text-[#C5701D] bg-[#FFF7ED] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{t("portal.roi.sizing_warnings_intro")} {roi.sizing.warnings.map(w => t(`portal.personal_consult.${w}`, { defaultValue: w })).join(" · ")}</span>
            </div>
          )}
        </div>
      )}

      {!lead.paid ? (
        <div className="bg-gradient-to-br from-[#E78938] to-[#C5701D] text-white rounded-xl p-7 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div>
              <div className="serif text-3xl mb-1">{t("portal.your_dossier")}</div>
              {promo?.ok && (
                <div className="text-sm text-white/90" data-testid="promo-active">
                  <span className="line-through opacity-70">€{reportPrice}</span>
                  &nbsp;<b>€{finalPrice}</b> · −€{promo.discount_eur} ({promo.code})
                  {lead.auto_promo_code === promo.code && (
                    <span className="ml-2 inline-flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full text-[11px]">
                      <Sparkles className="w-3 h-3" /> {t("portal.share.friend_discount_applied")}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex gap-2">
                <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Código"
                  data-testid="promo-input"
                  className="bg-white/20 border border-white/30 rounded-xl px-3 py-2.5 text-white placeholder:text-white/60 outline-none focus:bg-white/30 w-32 uppercase mono" />
                <button onClick={validatePromo} disabled={validatingPromo || !promoCode.trim()}
                  data-testid="promo-validate"
                  className="bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2.5 text-sm disabled:opacity-50">
                  {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "OK"}
                </button>
              </div>
              <button data-testid="pay-button" onClick={() => onPay(promo?.ok ? promo.code : null)} disabled={busy}
                className="bg-white text-[#E78938] hover:bg-[#FFFFFF] rounded-xl px-6 py-3 font-medium transition-all flex items-center gap-2 justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("portal.roi.pay_button", { price: finalPrice })} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-[#E78938] text-white rounded-xl p-7 flex justify-between items-center">
            <div>
              <div className="serif text-2xl">✓</div>
            </div>
            <button data-testid="download-pdf" onClick={onDownload}
              className="bg-white text-[#0F172A] rounded-xl px-6 py-3 font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" /> {t("portal.roi.download_pdf")}
            </button>
          </div>
          {shareUrl && (
            <div data-testid="share-block" className="mt-6 bg-white border-2 border-dashed border-[#E78938] rounded-2xl p-7">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#FFF7ED] flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-[#E78938]" />
                </div>
                <div>
                  <div className="serif text-xl text-[#0F172A]">{t("portal.share.title")}</div>
                  <p className="text-sm text-[#475569] mt-1">{t("portal.share.intro")}</p>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#E78938] font-semibold mb-1">{t("portal.share.code_label")}</div>
              <div className="mono text-2xl text-[#0F172A] tracking-[0.3em] mb-4 font-bold">{lead.referral_code}</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <input readOnly value={shareUrl} data-testid="share-url"
                  className="flex-1 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#0F172A] font-mono truncate" />
                <button data-testid="share-copy" onClick={copyShare}
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl px-5 py-3 text-sm font-medium flex items-center justify-center gap-2 transition">
                  {shareCopied ? <Check className="w-4 h-4" /> : null}
                  {shareCopied ? t("portal.share.copied") : t("portal.share.copy")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <><div className="text-[#475569]">{k}</div><div className="text-[#0F172A] font-medium">{v}</div></>
);
const Stat = ({ label, value, accent }) => (
  <div className={`rounded-xl p-5 ${accent ? "bg-[#E78938]/8 border border-[#E78938]/20" : "bg-[#FFF7ED]/50 border border-[#FFF7ED]"}`}>
    <div className="text-xs uppercase tracking-wider text-[#E78938]">{label}</div>
    <div className={`serif text-2xl tnum mt-1 ${accent ? "text-[#E78938]" : "text-[#0F172A]"}`}>{value}</div>
  </div>
);
const SizingRow = ({ k, v, accent }) => (
  <div className={`rounded-lg p-3 ${accent ? "bg-[#0F172A] text-white" : "bg-[#FFF7ED]/40 border border-[#FFF7ED]"}`}>
    <div className={`text-[10px] uppercase tracking-wider font-semibold ${accent ? "text-[#FED7AA]" : "text-[#E78938]"}`}>{k}</div>
    <div className={`text-sm tnum mt-0.5 ${accent ? "text-[#E78938] font-bold" : "text-[#0F172A]"}`}>{v}</div>
  </div>
);
